import { useEffect, useMemo } from 'react'
import { X, Loader2, CheckCircle, AlertCircle, User, Wand2, Film, BookOpen } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { useTasksStore, type RunningTask } from '@/stores/tasks'
import { useNavigate } from 'react-router-dom'

function getTaskIcon(taskType: string) {
  switch (taskType) {
    case 'digital_human':
      return <User className="w-4 h-4" />
    case 'image_freestyle':
      return <Wand2 className="w-4 h-4" />
    case 'video_freestyle':
      return <Film className="w-4 h-4" />
    case 'vertical_ideas':
      return <BookOpen className="w-4 h-4" />
    default:
      return <Wand2 className="w-4 h-4" />
  }
}

function getTaskLabel(taskType: string): string {
  switch (taskType) {
    case 'digital_human':
      return 'Digital Human'
    case 'image_freestyle':
      return 'Image Generation'
    case 'video_freestyle':
      return 'Video Generation'
    case 'competitor_research':
      return 'Competitor Research'
    case 'variation':
      return 'Variation'
    case 'vertical_ideas':
      return 'Vertical Ideas'
    default:
      return taskType.replace(/_/g, ' ')
  }
}

function TaskCard({
  task,
  className = '',
  moreCount = 0,
}: {
  task: RunningTask
  className?: string
  moreCount?: number
}) {
  const { removeTask } = useTasksStore()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const handleClick = () => {
    if (task.status === 'completed') {
      // Refresh Library data and navigate to Library
      queryClient.invalidateQueries({ queryKey: ['assets'] })
      queryClient.invalidateQueries({ queryKey: ['assets-stats'] })
      navigate('/library')
    } else {
      // Processing or failed tasks navigate to Tasks page
      navigate('/tasks')
    }
  }

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (task.status === 'completed' || task.status === 'failed') {
      removeTask(task.id)
    }
  }

  return (
    <div
      onClick={handleClick}
      className={`bg-white rounded-xl shadow-lg border border-gray-200/60 p-4 w-full cursor-pointer hover:shadow-xl transition-shadow ${className}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {task.status === 'processing' || task.status === 'pending' ? (
            <Loader2 className="w-4 h-4 text-primary animate-spin" />
          ) : task.status === 'completed' ? (
            <CheckCircle className="w-4 h-4 text-green-600" />
          ) : (
            <AlertCircle className="w-4 h-4 text-red-600" />
          )}
          <span className="font-medium text-sm flex items-center gap-1">
            {getTaskIcon(task.type)}
            {getTaskLabel(task.type)}
          </span>
        </div>
        <button
          onClick={handleDismiss}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          disabled={task.status === 'processing' || task.status === 'pending'}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {task.status === 'processing' && (
        <div className="space-y-1">
          {task.progressStage && (
            <p className="text-xs text-gray-600">
              {task.progressStage === 'script' && '📝 Generating script...'}
              {task.progressStage === 'images' && '🖼️ Generating images...'}
              {task.progressStage === 'videos' && '🎬 Generating videos...'}
            </p>
          )}
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div className="bg-primary h-1.5 rounded-full animate-pulse" style={{ width: '60%' }} />
          </div>
          <p className="text-xs text-gray-500">This may take a few minutes</p>
        </div>
      )}

      {task.status === 'completed' && (
        <p className="text-xs text-green-600">✓ Generation completed! Click to view in Library.</p>
      )}

      {task.status === 'failed' && (
        <p className="text-xs text-red-600">✗ {task.error || 'Generation failed'}</p>
      )}

      {moreCount > 0 && (
        <div className="mt-3 inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-[11px] font-medium text-gray-600">
          +{moreCount} more task{moreCount > 1 ? 's' : ''}
        </div>
      )}
    </div>
  )
}

export function TaskProgressToast() {
  const { tasks, startPolling, isPolling } = useTasksStore()

  // Resume polling for any processing tasks that don't have active polling
  // This handles the case when the page was refreshed or navigated away
  useEffect(() => {
    Object.values(tasks).forEach((task) => {
      if ((task.status === 'processing' || task.status === 'pending') && !isPolling(task.id)) {
        console.log('[TaskProgressToast] Resuming polling for task:', task.id)
        startPolling(task.id)
      }
    })
  }, [tasks, startPolling, isPolling])

  // Show all tasks in store - completed tasks are auto-cleaned after 30s by store
  const visibleTasks = useMemo(
    () => Object.values(tasks).sort((a, b) => b.createdAt - a.createdAt),
    [tasks]
  )
  const stackedTasks = visibleTasks.slice(0, 3)
  const hiddenTaskCount = Math.max(0, visibleTasks.length - stackedTasks.length)

  if (visibleTasks.length === 0) {
    return null
  }

  return (
    <div
      className="fixed bottom-6 right-6 z-50 w-[min(380px,calc(100vw-1.5rem))] pointer-events-none"
      style={{ height: `${132 + Math.max(0, stackedTasks.length - 1) * 16}px` }}
    >
      <div className="relative h-full">
        {[...stackedTasks].reverse().map((task, reverseIndex) => {
          const stackIndex = stackedTasks.length - reverseIndex - 1
          const isTopCard = stackIndex === 0

          return (
            <div
              key={task.id}
              className="absolute bottom-0 right-0 w-full"
              style={{
                transform: `translateY(-${stackIndex * 16}px) scale(${1 - stackIndex * 0.03})`,
                transformOrigin: 'bottom right',
                zIndex: 100 - stackIndex,
                opacity: 1 - stackIndex * 0.12,
              }}
            >
              <TaskCard
                task={task}
                className={isTopCard ? 'pointer-events-auto' : 'pointer-events-none'}
                moreCount={isTopCard ? hiddenTaskCount : 0}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
